import React, { FC, useEffect, useState } from 'react';
import s from '@modals/PaymentMethodEditModal/PaymentMethodEditModal.module.scss';
import Modal from '@components/Modal';
import { useIsMobile } from '@hooks';
import { mixpanelTrack } from '@providers/Mixpanel';
import {
  Stripe,
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
  StripeError,
} from '@stripe/stripe-js';
import { Creator } from '@typings/graphql-models';
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements } from '@stripe/react-stripe-js';
import { CARD_ELEMENT_OPTIONS } from '@helpers/checkout';
import Buttons from '@components/Buttons';
import Spacer from '@components/Spacer';
import { attachNewCard } from '@tsClient';
import useUserProfile from '@providers/Auth/useUserProfile';
import useNoodleApi from '@hooks/useNoodleApi';

type Props = {
  stripe: Stripe | null;
  isVisible: boolean;
  setIsVisible: (v: boolean) => void;
  creator: Pick<Creator, 'id' | 'stripe_account_id'> | null;
  onSaveCard?: () => Promise<void>;
};

type Event = StripeCardNumberElementChangeEvent | StripeCardCvcElementChangeEvent | StripeCardExpiryElementChangeEvent;

const PaymentMethodEditModal: FC<Props> = ({ stripe, creator, isVisible, setIsVisible, onSaveCard }) => {
  const [error, setError] = useState<StripeError | string | null>(null);
  const [isDisabled, setDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const isMobile = useIsMobile();
  const elements = useElements();
  const profile = useUserProfile();
  const { getData: attachNewCardFn } = useNoodleApi(attachNewCard);

  const handleCardEvent = async (event: Event): Promise<void> => {
    if (event.error) {
      setError(event.error.message);
      setDisabled(true);
    } else {
      setError('');
      setDisabled(event.empty);
    }
  };

  useEffect(() => {
    if (isVisible) {
      mixpanelTrack('Edit payment method');
    } else {
      setError(null);
      setIsLoading(false);
    }
  }, [isVisible]);

  const saveCard = async (): Promise<void> => {
    try {
      setIsLoading(true);
      if (!stripe || !elements) {
        throw new Error(`Stripe not initialized: stripe=${Boolean(stripe)} elements=${Boolean(elements)}`);
      }

      const card = elements?.getElement(CardNumberElement);

      if (!card) {
        throw new Error('Failed to get card from stripe');
      }
      const data = await stripe.createPaymentMethod({
        billing_details: {
          name: profile?.name || 'User',
        },
        card,
        type: 'card',
      });

      const { error: stripeError, paymentMethod } = data;

      if (stripeError) {
        mixpanelTrack('Error in attach card form', { errorReason: stripeError.message });
        setError(stripeError.message as string);
        return;
      }

      if (creator?.id && paymentMethod?.id && profile) {
        await attachNewCardFn({
          creatorId: creator.id,
          isDefaultPaymentMethod: true,
          paymentMethodId: paymentMethod?.id,
        });
        await onSaveCard?.();
        setIsVisible(false);
      }

      setIsLoading(false);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return isVisible
    ? (
      <Modal
        isPinnedToBottom={isMobile}
        title={'Edit payment method'}
        onClose={() => setIsVisible(false)}
        hasCloseOnActions={false}
      >
        <div className={s.container}>
          <p className='caption'>Card number</p>
          <Spacer size='8px' />
          <div className={s.input}>
            <CardNumberElement options={CARD_ELEMENT_OPTIONS} onChange={handleCardEvent} />
          </div>
          <Spacer />
          <div className={s.double}>
            <div style={{ width: '100%' }}>
              <p className='caption'>Expiration date</p>
              <Spacer size='8px' />
              <div className={s.input}>
                <CardExpiryElement options={CARD_ELEMENT_OPTIONS} onChange={handleCardEvent} />
              </div>
            </div>
            <div style={{ width: '100%' }}>
              <p className='caption'>CVC</p>
              <Spacer size='8px' />
              <div className={s.input}>
                <CardCvcElement options={CARD_ELEMENT_OPTIONS} onChange={handleCardEvent} />
              </div>
            </div>
          </div>
          {error && <p className={s.error}>{error}</p>}
          <Spacer size='24px' />
          <Buttons
            isSecondary
            isFullWidth
            disabled={isDisabled || Boolean(error)}
            onClick={saveCard}
            isFetching={isLoading}
          >
            Save
          </Buttons>
        </div>
      </Modal>
    )
    : null;
};

export default PaymentMethodEditModal;
